import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AlertBanner from './components/layout/AlertBanner';

// Utility components
import ErrorBoundary from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/Toast';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { MemberAuthProvider } from './contexts/MemberAuthContext';
import { ScrollToTop, BackToTop } from './components/common/ScrollUtils';
import { SkipLink } from './components/common/Accessibility';
import Loading from './components/common/Loading';
import RouteTitle from './components/common/RouteTitle';

// Eagerly loaded pages (critical path)
import Home from './pages/Home';
import { NotFound } from './pages/Placeholder';

// Admin routes (lazy loaded)
const AdminRoutes = lazyWithRetry(() => import('./pages/admin/AdminRoutes'));

// Lazy loaded pages (code splitting for performance)
const About = lazyWithRetry(() => import('./pages/About'));
const Beliefs = lazyWithRetry(() => import('./pages/Beliefs'));
const Leadership = lazyWithRetry(() => import('./pages/Leadership'));
const Sermons = lazyWithRetry(() => import('./pages/Sermons'));
const SermonDetail = lazyWithRetry(() => import('./pages/SermonDetail'));
const Events = lazyWithRetry(() => import('./pages/Events'));
const EventDetail = lazyWithRetry(() => import('./pages/EventDetail'));
const Announcements = lazyWithRetry(() => import('./pages/Announcements'));
const AnnouncementDetail = lazyWithRetry(() => import('./pages/AnnouncementDetail'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Livestream = lazyWithRetry(() => import('./pages/Livestream'));
const Podcast = lazyWithRetry(() => import('./pages/Podcast'));
const Give = lazyWithRetry(() => import('./pages/Give'));
const SmallGroups = lazyWithRetry(() => import('./pages/SmallGroups'));
const ThemeTest = lazyWithRetry(() => import('./pages/ThemeTest'));
const Page = lazyWithRetry(() => import('./pages/Page'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const Catechisms = lazyWithRetry(() => import('./pages/Catechisms'));
const SetPassword = lazyWithRetry(() => import('./pages/SetPassword'));
const TvDisplay = lazyWithRetry(() => import('./pages/TvDisplay'));
const FellowshipTv = lazyWithRetry(() => import('./pages/FellowshipTv'));
const Rooms = lazyWithRetry(() => import('./pages/Rooms'));
const Bulletin = lazyWithRetry(() => import('./pages/Bulletin'));
const Music = lazyWithRetry(() => import('./pages/Music'));
const MusicAlbum = lazyWithRetry(() => import('./pages/MusicAlbum'));
const Fellowship = lazyWithRetry(() => import('./pages/Fellowship'));
const Ministries = lazyWithRetry(() => import('./pages/Ministries'));
const Bible = lazyWithRetry(() => import('./pages/Bible'));
const Privacy = lazyWithRetry(() => import('./pages/Privacy'));

// Newsletter pages
const NewsletterSubscribe = lazyWithRetry(() => import('./pages/NewsletterSubscribe'));
const NewsletterConfirm = lazyWithRetry(() => import('./pages/NewsletterConfirm'));
const NewsletterUnsubscribe = lazyWithRetry(() => import('./pages/NewsletterUnsubscribe'));
const NewsletterPreferences = lazyWithRetry(() => import('./pages/NewsletterPreferences'));

// Member pages
const MemberLogin = lazyWithRetry(() => import('./pages/MemberLogin'));
const MemberSignup = lazyWithRetry(() => import('./pages/MemberSignup'));
const MemberVerifyEmail = lazyWithRetry(() => import('./pages/MemberVerifyEmail'));
const MemberForgotPassword = lazyWithRetry(() => import('./pages/MemberForgotPassword'));
const MemberResetPassword = lazyWithRetry(() => import('./pages/MemberResetPassword'));
const MemberProfile = lazyWithRetry(() => import('./pages/MemberProfile'));
const Conversations = lazyWithRetry(() => import('./pages/Conversations'));
const ConversationSettings = lazyWithRetry(() => import('./pages/ConversationSettings'));
const MemberPreferences = lazyWithRetry(() => import('./pages/MemberPreferences'));
const MemberChangePassword = lazyWithRetry(() => import('./pages/MemberChangePassword'));
const MemberRegistrations = lazyWithRetry(() => import('./pages/MemberRegistrations'));
const MemberResources = lazyWithRetry(() => import('./pages/MemberResources'));
const PublicResources = lazyWithRetry(() => import('./pages/PublicResources'));

// Music components
import { MusicPlayerProvider } from './components/music/MusicPlayerProvider';
import { ChatProvider } from './contexts/ChatContext';
import ConversationsPanel from './components/chat/ConversationsPanel';
import MusicPlayer from './components/music/MusicPlayer';

// Page loading fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <Loading text="Loading..." />
    </div>
  );
}

// Public site layout wrapper
function PublicLayout({ children }) {
  const { navigationSettings } = useSettings();
  const isLightText = navigationSettings.text_theme === 'light';
  return (
    <>
      <SkipLink />
      <div className={`flex flex-col min-h-screen${isLightText ? ' bg-primary' : ''}`}>
        <AlertBanner />
        <Header />
        <div className="flex-grow">
          {children}
        </div>
        <Footer />
      </div>
      <BackToTop />
    </>
  );
}

// App layout wrapper - no navigation or footer
function AppLayout({ children }) {
  return (
    <div className="min-h-screen">
      {children}
    </div>
  );
}

// Shared page routes using relative paths so they work under both /* and /app/*
function SiteRoutes() {
  return (
    <Routes>
      {/* Home */}
      <Route index element={<Home />} />

      {/* About Section */}
      <Route path="about" element={<About />} />
      <Route path="about/beliefs" element={<Beliefs />} />
      <Route path="about/leadership" element={<Leadership />} />

      {/* Sermons */}
      <Route path="sermons" element={<Sermons />} />
      <Route path="sermons/:slug" element={<SermonDetail />} />

      {/* Events */}
      <Route path="events" element={<Events />} />
      <Route path="events/:slug" element={<EventDetail />} />

      {/* Announcements */}
      <Route path="announcements" element={<Announcements />} />
      <Route path="announcements/:slug" element={<AnnouncementDetail />} />

      {/* Ministries */}
      <Route path="ministries" element={<Ministries />} />

      {/* Connect */}
      <Route path="small-groups" element={<SmallGroups />} />
      <Route path="contact" element={<Contact />} />
      <Route path="fellowship" element={<Fellowship />} />

      {/* Member */}
      <Route path="member/profile" element={<MemberProfile />} />
      <Route path="member/preferences" element={<MemberPreferences />} />
      <Route path="member/password" element={<MemberChangePassword />} />
      <Route path="member/registrations" element={<MemberRegistrations />} />
      <Route path="member/resources" element={<MemberResources />} />

      {/* Conversations — deep-link targets for digest email and push */}
      <Route path="conversations" element={<Conversations />} />
      <Route path="conversations/settings" element={<ConversationSettings />} />

      {/* Worship */}
      <Route path="livestream" element={<Livestream />} />
      <Route path="bulletin" element={<Bulletin />} />

      {/* Resources */}
      <Route path="resources" element={<PublicResources />} />
      <Route path="catechisms" element={<Catechisms />} />
      <Route path="bible" element={<Bible />} />
      <Route path="bible/:book/:chapter" element={<Bible />} />
      <Route path="podcast" element={<Podcast />} />
      <Route path="blog" element={<Blog />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      <Route path="music" element={<Music />} />
      <Route path="music/:slug" element={<MusicAlbum />} />

      {/* Give */}
      <Route path="give" element={<Give />} />

      {/* Privacy Policy */}
      <Route path="privacy" element={<Privacy />} />

      {/* Newsletter */}
      <Route path="newsletter" element={<NewsletterSubscribe />} />
      <Route path="newsletter/confirm/:token" element={<NewsletterConfirm />} />
      <Route path="newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
      <Route path="newsletter/preferences/:token" element={<NewsletterPreferences />} />

      {/* Theme Test (Development) */}
      <Route path="theme-test" element={<ThemeTest />} />

      {/* Custom Pages - Must be near the end to avoid route conflicts */}
      <Route path="pages/:slug" element={<Page />} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <SettingsProvider>
          <MemberAuthProvider>
            {/* Inside MemberAuthProvider: the socket only opens for an
                authenticated member, and chat has no guest mode. */}
            <ChatProvider>
            <MusicPlayerProvider>
              <BrowserRouter>
              <ScrollToTop />
              <RouteTitle />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Admin routes - separate from public site */}
                <Route path="/admin/*" element={<AdminRoutes />} />

                {/* Set password page (no layout - like login) */}
                <Route path="/set-password" element={<SetPassword />} />

                {/* TV Display routes - full screen, no layout */}
                <Route path="/tv/:slug" element={<TvDisplay />} />
                <Route path="/fellowship-tv" element={<FellowshipTv />} />

                {/* Rooms display - full screen, no layout */}
                <Route path="/rooms" element={<Rooms />} />

                {/* Member auth pages (no layout - like login) */}
                <Route path="/member/login" element={<MemberLogin />} />
                <Route path="/member/signup" element={<MemberSignup />} />
                <Route path="/member/verify-email" element={<MemberVerifyEmail />} />
                <Route path="/member/forgot-password" element={<MemberForgotPassword />} />
                <Route path="/member/reset-password" element={<MemberResetPassword />} />

                {/* App routes - no navigation or footer */}
                <Route path="/app/*" element={
                  <AppLayout>
                    <SiteRoutes />
                  </AppLayout>
                } />

                {/* Public routes with layout */}
                <Route path="/*" element={
                  <PublicLayout>
                    <SiteRoutes />
                  </PublicLayout>
                } />
                </Routes>
                <MusicPlayer />
              </Suspense>

              {/* Outside the Suspense boundary on purpose.
                  Inside it, every navigation to a not-yet-loaded lazy route
                  suspends the boundary and replaces ALL its children with the
                  page loader — so the panel vanished mid-conversation and
                  remounted with its local state reset. Out here it is unaffected
                  by route loading. */}
              <ConversationsPanel />
            </BrowserRouter>
            </MusicPlayerProvider>
            </ChatProvider>
          </MemberAuthProvider>
        </SettingsProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
